import { DirectMessages, DMConversation } from "0.Mine/3.Interfaces/DMResponse";
import { EmailPreview } from "0.Mine/3.Interfaces/Email";
import { Conversation } from "0.Mine/3.Interfaces/facebookconversation";
import { Inbox } from "0.Mine/3.Interfaces/GMBReview";
import {
  Message,
  WhatsappConversationResponse,
  WhatsappResponse,
} from "0.Mine/3.Interfaces/whatsappResponse";

export function MapEmailToConversation(emails: EmailPreview[]) {
  const conversations: any[] = [];
  emails.forEach((email) => {
    if (email.subject.includes("SPAM")) return;
    const conversation = {
      id: email.msgno,
      contactId: email.from,
      channel: "email",
      unreadCount: email.seen ? 0 : 1,
      contact: {
        name: email.from,
        avatar: "https://via.placeholder.com/150",
      },
      lastMessage: {
        text: email.subject,
        time: new Date(email.date).toLocaleDateString(),
        isOutgoing: false,
        status: null,
      },
      messages: [],
    };
    conversations.push(conversation);
  });

  //sort them in chronological order, the most recent first
  conversations.sort((a, b) => {
    const dateA = new Date(a.lastMessage.time).getTime();
    const dateB = new Date(b.lastMessage.time).getTime();
    return dateB - dateA;
  });

  return conversations;
}

export function MapFacebookToConversation(facebook: Conversation[]) {
  const conversations: any[] = [];

  facebook.forEach((conversation) => {
    const conv = {
      id: conversation.id,
      contactId: conversation.participants[0].id,
      channel: "facebook",
      unreadCount: 0,
      contact: {
        name: conversation.participants[0].name,
        avatar: "https://via.placeholder.com/150",
      },
      lastMessage: {
        text: conversation.messages[conversation.messages.length - 1].message,
        time: new Date(
          conversation.messages[conversation.messages.length - 1].created_time
        ).toLocaleDateString(),
        isOutgoing: false,
        status: null,
      },
      messages: [],
    };
    conversations.push(conv);
  });

  return conversations;
}

export function MapFacebookToMessages(conversation: Conversation) {
  const messages: any = {
    id: conversation.id,
    contact_id: conversation.participants[0].id,
    channel: "facebook",
    unreadCount: 0,
    contact: {
      name: conversation.participants[0].name,
      avatar: "https://via.placeholder.com/150",
    },
    lastMessage: {
      text: conversation.messages[conversation.messages.length - 1].message,
      time: new Date(
        conversation.messages[conversation.messages.length - 1].created_time
      ).toLocaleDateString(),
      isOutgoing: false,
      status: null,
    },
    messages: [],
  };

  // Copy messages into a new array so we can sort without modifying the original
  const sortedMessages = [...conversation.messages];

  // Sort by created_time (oldest first)
  sortedMessages.sort((a, b) => {
    const timeA = new Date(a.created_time).getTime();
    const timeB = new Date(b.created_time).getTime();
    return timeA - timeB;
  });

  // Now add the sorted messages to our result
  sortedMessages.forEach((message) => {
    const msg = {
      text: message.message,
      time: new Date(message.created_time).toLocaleDateString(),
      isOutgoing: message.from.id !== conversation.participants[0].id,
    };
    messages.messages.push(msg);
  });

  console.log("FACEBOOK CONVO: ", messages);
  return messages;
}

export function MapGMBReviewToConversation(gmb: any) {
  const conversations: any[] = [];

  // Check if the response has the expected structure
  if (!gmb?.reviews) {
    console.error("Invalid GMB response structure");
    return conversations;
  }

  const reviews = gmb.reviews;

  reviews.forEach((review: any) => {
    // Find the reviewer from participants
    const reviewer = review.participants.find((p: any) => p.id !== review.self);

    const reviewDate = new Date(review.creationDate);

    const messages = [
      {
        id: `${review.id}-review`,
        text: review.message || "",
        time: reviewDate.toLocaleDateString(),
        isOutgoing: false,
        status: "READ",
        stars: review.stars,
      },
    ];

    // Add reply if it exists
    if (review.reply) {
      messages.push({
        id: `${review.id}-reply`,
        text: review.reply.comment || "",
        time: new Date(review.reply.updateTime).toLocaleDateString(),
        isOutgoing: true,
        status: "READ",
        stars: review.stars,
      });
    }

    const conv = {
      id: review.id,
      contactId: reviewer?.id || "",
      channel: "gmb",
      unreadCount: review.status === "UNREAD" ? 1 : 0,
      contact: {
        name: reviewer?.name || "Unknown",
        avatar: reviewer?.imageProfileUrl || "https://via.placeholder.com/150",
      },
      stars: review.stars,
      lastMessage: {
        text: review.message || "",
        time: reviewDate.toLocaleDateString(),
        isOutgoing: false,
        status: review.status,
        stars: review.stars,
      },
      messages: messages,
    };

    conversations.push(conv);
  });

  return conversations;
}

export function MapInstagramToConversation(Instagram: DirectMessages) {
  const conversations: any[] = [];

  // Check if the response has the expected structure
  if (!Instagram?.conversations) {
    console.error("Invalid Instagram response structure");
    return conversations;
  }

  Instagram.conversations.forEach((conversation: DMConversation) => {
    // Find the participant that is not self (the contact)
    const contact = conversation.participants.find((p: any) => p.id !== conversation.self);

    // Only proceed if we have messages
    if (conversation.messages && conversation.messages.length > 0) {
      // Get the last message - still the first in the array (newest)
      const lastMsg = conversation.messages[0];

      // Create a copy of messages and sort them (oldest first)
      const sortedMessages = [...conversation.messages].sort((a, b) => {
        const timeA = new Date(a.publicationDateTime).getTime();
        const timeB = new Date(b.publicationDateTime).getTime();
        return timeA - timeB; // Ascending order (oldest first)
      });

      const conv = {
        id: conversation.id,
        contactId: contact?.id || "",
        channel: "instagram",
        unreadCount: conversation.status === "UNREAD" ? 1 : 0,
        contact: {
          name: contact?.name || "Unknown",
          avatar: "https://via.placeholder.com/150",
          recipient: contact?.id || "",
        },
        lastMessage: {
          text: lastMsg.text,
          time: new Date(lastMsg.publicationDateTime).toLocaleDateString(),
          isOutgoing: lastMsg.from === conversation.self,
          status: lastMsg.status,
        },
        messages: sortedMessages.map((msg: any) => ({
          id: msg.id,
          text: msg.text,
          time: new Date(msg.publicationDateTime).toLocaleDateString(),
          isOutgoing: msg.from === conversation.self,
          status: msg.status,
          attachments: msg.attachments || [],
        })),
      };
      conversations.push(conv);
    }
  });

  return conversations;
}

export function MapWhatsappToConversation(whatsapp: WhatsappResponse) {
  const conversations: any[] = [];

  // Check if the response has the expected structure
  if (!whatsapp?.data?.contacts) {
    console.error("Invalid Whatsapp response structure");
    return conversations;
  }

  whatsapp.data.contacts.contacts.forEach((contact: any) => {
    const conv = {
      id: contact.id,
      contactId: contact.id,
      channel: "whatsapp",
      unreadCount: 0,
      contact: {
        name: contact.name,
        avatar: "https://via.placeholder.com/150",
      },
      lastMessage: {
        text: "Clicca per vedere la chat",
        time: new Date().toLocaleDateString(),
        isOutgoing: false,
        status: null,
      },
      messages: [],
    };
    conversations.push(conv);
  });

  return conversations;
}

export const mapWhatsappToMessages = (whatsapp: WhatsappConversationResponse) => {
  // Check if the response has the expected structure
  if (!whatsapp?.data?.messages) {
    console.error("Invalid WhatsApp conversation response structure");
    return [];
  }

  // Transform the messages into the format your app expects
  const formattedMessages = whatsapp.data.messages.map((message: Message) => {
    return {
      id: message.id,
      text: message.body || "",
      time: new Date(message.timestamp * 1000).toLocaleDateString(),
      isOutgoing: message.fromMe,
      status: null,
    };
  });

  // Sort by timestamp then reverse the array
  return formattedMessages.reverse();
};
